import {
    Checkbox,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { config } from 'src/lib/config';
import { windowsOverlap } from './dateOverlap';

type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;

export interface GroupedCultures {
    [region: string]: string[];
}

const { groupedLocales, locales: allLocales } = config as unknown as {
    groupedLocales: GroupedCultures;
    locales: string[];
};

interface CulturePickerProps {
    cultures: string[];
    groupIndex: number;
    cultureGroups: SetsConfigCultureGroup[];
    availableCultures?: string[];
    error?: boolean;
    helperText?: string;
    onChange: (cultures: string[]) => void;
}

const getUnavailabilityCaption = (isClaimed: boolean, isUnsold: boolean): string | null => {
    if (isClaimed) return 'in use by an overlapping group';
    if (isUnsold) return 'not sold in this culture';
    return null;
};

export const CulturePicker = ({
    cultures,
    groupIndex,
    cultureGroups,
    availableCultures = allLocales,
    error = false,
    helperText = ' ',
    onChange,
}: CulturePickerProps): JSX.Element => {
    const currentGroup = cultureGroups[groupIndex];
    const currentWindow = {
        startDate: currentGroup?.startDate ?? null,
        endDate: currentGroup?.endDate ?? null,
    };

    // A culture is unavailable to this group only when another group already claims it during an
    // overlapping active window; the same culture can live in multiple groups as long as their
    // windows never run concurrently.
    const isClaimedByOverlappingGroup = (culture: string): boolean => cultureGroups.some(
        (otherGroup, otherIndex) => otherIndex !== groupIndex
            && otherGroup.cultures.includes(culture)
            && windowsOverlap(currentWindow, { startDate: otherGroup.startDate, endDate: otherGroup.endDate }),
    );

    const isSoldInCulture = (culture: string): boolean => availableCultures.includes(culture);

    const handleCultureToggle = (culture: string): void => {
        const isSelected = cultures.includes(culture);
        const nextCultures = isSelected
            ? cultures.filter((selected) => selected !== culture)
            : [...cultures, culture];

        onChange(nextCultures);
    };

    // The Select only anchors the menu; every mutation happens through onClick handlers on the
    // checkboxes/menu items below, matching the RankingTabs culture picker.
    const handleNoOp = (_event: SelectChangeEvent<string>): void => undefined;

    return (
        <FormControl size="small" sx={{ maxWidth: 400, width: '100%' }}>
            <InputLabel id={`culture-picker-label-${groupIndex}`}>Cultures</InputLabel>
            <Select
                displayEmpty
                error={error}
                label="Cultures"
                labelId={`culture-picker-label-${groupIndex}`}
                renderValue={(): string => (cultures.length > 0 ? cultures.join(', ') : 'Select cultures')}
                value="culture-picker"
                onChange={handleNoOp}
            >
                {Object.entries(groupedLocales).flatMap(([region, regionLocales]) => {
                    const selectableLocales = regionLocales.filter(
                        (culture) => !isClaimedByOverlappingGroup(culture) && isSoldInCulture(culture),
                    );
                    const selectedInRegion = regionLocales.filter((culture) => cultures.includes(culture));
                    const allSelected = selectableLocales.length > 0
                        && selectableLocales.every((culture) => cultures.includes(culture));
                    const someSelected = selectedInRegion.length > 0 && !allSelected;

                    const handleRegionToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
                        const { checked } = event.target;
                        const nextCultures = checked
                            ? [...new Set([...cultures, ...selectableLocales])]
                            : cultures.filter((culture) => !selectableLocales.includes(culture));

                        onChange(nextCultures);
                    };

                    return [
                        <ListSubheader key={region} sx={{ pl: 1 }}>
                            <Checkbox
                                checked={allSelected}
                                disabled={selectableLocales.length === 0}
                                indeterminate={someSelected}
                                onChange={handleRegionToggle}
                            />
                            {region}
                        </ListSubheader>,
                        ...regionLocales.map((culture) => {
                            const isSelected = cultures.includes(culture);
                            const isClaimed = !isSelected && isClaimedByOverlappingGroup(culture);
                            const isUnsold = !isSoldInCulture(culture);
                            // Selected cultures stay deselectable even when unsold, so older saved configs can be fixed.
                            const isDisabled = isClaimed || (!isSelected && isUnsold);
                            const unavailabilityCaption = getUnavailabilityCaption(isClaimed, isUnsold);

                            return (
                                <MenuItem
                                    disabled={isDisabled}
                                    key={culture}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                    value={culture}
                                    onClick={(): void => handleCultureToggle(culture)}
                                >
                                    <Checkbox checked={isSelected} />
                                    <span>{culture}</span>
                                    {unavailabilityCaption && (
                                        <Typography color="text.secondary" sx={{ ml: 1 }} variant="caption">
                                            {unavailabilityCaption}
                                        </Typography>
                                    )}
                                </MenuItem>
                            );
                        }),
                    ];
                })}
            </Select>
            <Typography color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, mx: 1.75 }} variant="caption">
                {helperText}
            </Typography>
        </FormControl>
    );
};
